import './style.css'
import '@tempots/beatui/tailwind.css'
import { BeatUI } from '@tempots/beatui'
import { RootRouter } from '@tempots/ui'

// Auth pages
import {
  SigninPage,
  SignupPage,
  ForgotPasswordPage,
  VerifyEmailPage,
  VerifyEmailSentPage,
  ResetPasswordPage,
  VerifyEmailChangePage,
} from './pages/auth'

// Dashboard pages
import { DashboardPage } from './pages/dashboard'
import { ApiKeysPage, ApiKeyDetailPage } from './pages/api-keys'
import { FilesPage, FileDetailPage } from './pages/files'
import { ActionsPage, ActionDetailPage } from './pages/actions'
import { JobsPage, JobDetailPage } from './pages/jobs'
import {
  OrganizationsPage,
  OrganizationDetailPage,
  OrganizationBillingPage,
  OrganizationMembersPage,
} from './pages/organizations'
import { ProjectsPage, ProjectMembersPage } from './pages/projects'
import { ProfilePage } from './pages/profile'

// Other pages
import { NotFoundPage } from './pages/not-found'
import { MarkdownPage } from './pages/markdown-page'
import { RootRedirect } from './pages/root-redirect'

// Components
import { AuthGuard } from './components/auth'

export function App() {
  return BeatUI(
    {
      enableAppearance: false,
      includeNotifications: true,
      includeAuthI18n: true,
    },
    RootRouter({
      // Root - redirect to current project or org selection
      '/': () => AuthGuard({}, RootRedirect),

      // Auth routes (public)
      '/signin': () => SigninPage(),
      '/signup': () => SignupPage(),
      '/forgot-password': () => ForgotPasswordPage(),
      '/verify-email': () => VerifyEmailPage(),
      '/verify-email-sent': () => VerifyEmailSentPage(),
      '/verify-email-change': () => VerifyEmailChangePage(),
      '/reset-password': () => ResetPasswordPage(),
      '/terms-of-service': () =>
        MarkdownPage({ url: '/content/terms-of-service.md' }),
      '/privacy-policy': () =>
        MarkdownPage({ url: '/content/privacy-policy.md' }),

      // Project-scoped routes: /p/{projectId}/...
      '/p/:projectId': (info) =>
        AuthGuard({}, () =>
          DashboardPage({ projectId: info.$.params.$.projectId })
        ),
      '/p/:projectId/files': (info) =>
        AuthGuard({}, () =>
          FilesPage({ projectId: info.$.params.$.projectId })
        ),
      '/p/:projectId/files/:hash': (info) =>
        AuthGuard({}, () =>
          FileDetailPage({
            projectId: info.$.params.$.projectId,
            id: info.$.params.$.hash,
          })
        ),
      '/p/:projectId/api-keys': (info) =>
        AuthGuard({}, () =>
          ApiKeysPage({ projectId: info.$.params.$.projectId })
        ),
      '/p/:projectId/api-keys/:id': (info) =>
        AuthGuard({}, () =>
          ApiKeyDetailPage({
            projectId: info.$.params.$.projectId,
            id: info.$.params.$.id,
          })
        ),
      '/p/:projectId/jobs': (info) =>
        AuthGuard({}, () => JobsPage({ projectId: info.$.params.$.projectId })),
      '/p/:projectId/jobs/:id': (info) =>
        AuthGuard({}, () =>
          JobDetailPage({
            projectId: info.$.params.$.projectId,
            id: info.$.params.$.id,
          })
        ),
      '/p/:projectId/actions': (info) =>
        AuthGuard({}, () =>
          ActionsPage({ projectId: info.$.params.$.projectId })
        ),
      '/p/:projectId/actions/:name': (info) =>
        AuthGuard({}, () =>
          ActionDetailPage({
            projectId: info.$.params.$.projectId,
            id: info.$.params.$.name,
          })
        ),
      '/p/:projectId/members': (info) =>
        AuthGuard({}, () =>
          ProjectMembersPage({ projectId: info.$.params.$.projectId })
        ),
      '/p/:projectId/members/:userId': (info) =>
        AuthGuard({}, () =>
          ProjectMembersPage({
            projectId: info.$.params.$.projectId,
            memberId: info.$.params.$.userId,
          })
        ),

      // Organization-scoped routes: /o/{orgId}/...
      '/o/:orgId': (info) =>
        AuthGuard({}, () =>
          OrganizationDetailPage({ id: info.$.params.$.orgId })
        ),
      '/o/:orgId/billing': (info) =>
        AuthGuard({}, () =>
          OrganizationBillingPage({ id: info.$.params.$.orgId })
        ),
      '/o/:orgId/members': (info) =>
        AuthGuard({}, () =>
          OrganizationMembersPage({ id: info.$.params.$.orgId })
        ),
      '/o/:orgId/members/:userId': (info) =>
        AuthGuard({}, () =>
          OrganizationMembersPage({
            id: info.$.params.$.orgId,
            memberId: info.$.params.$.userId,
          })
        ),
      '/o/:orgId/projects': (info) =>
        AuthGuard({}, () => ProjectsPage({ orgId: info.$.params.$.orgId })),

      // Global management routes
      '/organizations': () => AuthGuard({}, OrganizationsPage),

      // User profile
      '/profile': () => AuthGuard({}, ProfilePage),

      // Legacy routes - redirect to new structure (for backwards compatibility)
      '/dashboard': () => AuthGuard({}, RootRedirect),
      '/api-keys': () => AuthGuard({}, RootRedirect),
      '/files': () => AuthGuard({}, RootRedirect),
      '/actions': () => AuthGuard({}, RootRedirect),
      '/jobs': () => AuthGuard({}, RootRedirect),

      // Fallback
      '*': () => NotFoundPage(),
    })
  )
}
